<template>

  <div id="ai-jobs" class="ai-importer" v-if="user_confirmed">

    <h2>Auto-import and translation jobs</h2>

    <ErrorNotice v-if="error" :message="error" />

    <p class="explainer">Your menu imports, menu updates and translations from the last 30 days. Long runs keep going if you leave the page; open one here to pick it up where it is.</p>

    <div class="status" v-if="loading">
      <div class="spinner"></div>
      <p>Loading…</p>
    </div>

    <p class="empty" v-if="!loading && jobs.length === 0">No auto-import or translation jobs yet.</p>

    <div class="job" v-for="job in jobs" :key="job.job_id">

      <div class="job-main">
        <p class="job-title">{{ kindLabel(job.kind) }} <span class="job-menu" v-if="job.menu_name">· {{ job.menu_name }}</span></p>
        <p class="job-meta">
          Started {{ timeAgo(job.created_at) }}
          <span v-if="job.summary"> · {{ job.summary }}</span>
          <span v-if="isAdmin() && job.cost_usd"> · AI cost {{ formatCost(job.cost_usd) }}</span>
        </p>
        <p class="job-error" v-if="job.error">{{ job.error }}</p>
      </div>

      <span class="job-state" :class="`job-state-${stateOf(job)}`">
        <span class="mini-spinner" v-if="stateOf(job) === 'running'"></span>{{ stateLabel(job) }}
      </span>

      <div class="job-actions">
        <button v-if="canOpen(job)" @click="open(job)">{{ stateOf(job) === 'ready' ? 'Review' : 'Open' }}</button>
        <button class="secondary" v-if="canDismiss(job)" @click="dismiss(job)">Dismiss</button>
      </div>

    </div>

  </div>

</template>


<script>
import signedInOnly from './signedInOnly'
import ErrorNotice from './ErrorNotice'

const KIND_LABELS = { import: 'Menu import', update: 'Menu update', translation: 'Auto-translate' }
const REFRESH_INTERVAL = 5000

// The signed-in user's recent AI runs (stored server-side in ai_jobs), so a long run can be left and
// found again: Open goes back to its page, which picks the run up (see resumeJob in those pages).
export default {
  name: 'AiJobs',
  components: { ErrorNotice },
  mixins: [signedInOnly],
  data() {
    return {
      jobs: [],
      loading: true,
      error: '',
      refresh_timer: null
    }
  },
  beforeDestroy() {
    clearTimeout(this.refresh_timer)
  },
  methods: {
    onUserConfirmed() {
      this.load()
    },
    menuId() {
      return null
    },
    redirectToMenu() {
      this.$router.replace('/')
    },
    load() {
      clearTimeout(this.refresh_timer)
      this.$http.secured.get('/app_ai_jobs')
        .then(response => {
          this.jobs = response.data.jobs
          this.error = ''
          // keep the list current while something is still running
          if (this.jobs.some(job => job.status === 'running')) {
            this.refresh_timer = setTimeout(() => this.load(), REFRESH_INTERVAL)
          }
        })
        .catch(() => { this.error = "Couldn't load your auto-import and translation jobs. Please reload the page." })
        .finally(() => { this.loading = false })
    },
    // running | ready (finished, waiting for the user) | failed | applied | discarded
    stateOf(job) {
      if (job.status === 'running') return 'running'
      if (job.status === 'failed') return 'failed'
      if (job.review_state === 'pending') return 'ready'
      return job.review_state
    },
    stateLabel(job) {
      return { running: 'Running', ready: 'Ready to review', failed: 'Failed', applied: 'Applied', discarded: 'Discarded' }[this.stateOf(job)]
    },
    kindLabel(kind) {
      return KIND_LABELS[kind] || kind
    },
    canOpen(job) {
      return job.menu_id && ['running', 'ready'].includes(this.stateOf(job))
    },
    // translations are accepted or rejected on their own page
    canDismiss(job) {
      return job.kind !== 'translation' && this.stateOf(job) === 'ready'
    },
    open(job) {
      const routes = {
        import: `/photo_import/${job.menu_id}?job=${job.job_id}`,
        update: `/menu_update/${job.menu_id}?job=${job.job_id}`,
        translation: `/auto_translate/${job.menu_id}`
      }
      this.$router.push(routes[job.kind])
    },
    dismiss(job) {
      if (!confirm('Dismiss this result? It will no longer be waiting for review.')) {
        return
      }
      this.$http.secured.post(`/app_ai_jobs/${job.job_id}/dismiss`)
        .then(() => this.load())
        .catch(() => { this.error = "Couldn't dismiss that job. Please try again." })
    },
    // AI costs are shown to admins only (the server leaves them out for everyone else too)
    isAdmin() {
      return this.$store.getters.getUserType === 'admin'
    },
    formatCost(usd) {
      const amount = Number(usd)
      return `$${amount.toFixed(amount >= 0.1 ? 2 : 4)}`
    },
    timeAgo(value) {
      const seconds = Math.max(0, (Date.now() - new Date(value).getTime()) / 1000)
      if (seconds < 60) return 'just now'
      if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`
      if (seconds < 86400) return `${Math.round(seconds / 3600)} h ago`
      return new Date(value).toLocaleDateString()
    }
  }
}
</script>


<style scoped src="./importer.css"></style>

<style scoped>

.empty {
  color: dimgray;
}

.job {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 16px;
  box-sizing: border-box;
  padding: 16px;
  margin-bottom: 10px;
  background: white;
  border: 1px solid #e6e6e6;
  border-radius: 8px;
}

.job-main {
  min-width: 0;
}

.job-title {
  font-weight: 600;
  margin: 0px;
}

.job-menu {
  font-weight: 400;
  color: #505050;
}

.job-meta {
  font-size: 12.5px;
  color: dimgray;
  margin: 4px 0px 0px 0px;
}

.job-error {
  font-size: 12.5px;
  color: #BE1E2D;
  margin: 4px 0px 0px 0px;
}

.job-state {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
}

.job-state-running { background: rgba(39, 170, 225, 0.12); color: #276175; }
.job-state-ready { background: rgba(57, 181, 74, 0.15); color: #1d6b28; }
.job-state-failed { background: #fdecea; color: #BE1E2D; }
.job-state-applied, .job-state-discarded { background: #f0f0f0; color: #505050; }

.mini-spinner {
  width: 10px;
  height: 10px;
  border: 2px solid rgba(39, 170, 225, 0.3);
  border-top-color: rgba(39, 170, 225, 1);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.job-actions {
  display: flex;
  flex-direction: row;
  gap: 8px;
}

.job-actions button {
  font-size: 13px;
  padding: 7px 14px;
}

.job-actions button.secondary {
  background: white;
  border-color: #cccccc;
  color: #505050;
}

@media(max-width: 600px) {
  .job {
    grid-template-columns: 1fr;
    gap: 10px;
  }
}

</style>
